import { isDev } from "../../components/common/Common";
import { BASE_URL, ContentDisposition, ContentType, DEV_BASE_URL, Method } from "../Config";

interface ResponseType {
    flag: boolean;
    status?: number;
    code?: string; // 불필요하지만 기존 API에서 사용해서 유지함
    desc?: string;
    result?: any;
    filename?: string;
}

const enum StatusCodes {
    OK = 200,
    NO_CONTENT = 204,
    BAD_REQUEST = 400,
    NOT_FOUND = 404,
    SERVER_ERROR = 500,
}

abstract class Gateway {
    protected abstract _contextPath: string;
    protected abstract _pathParams?: { [key: string]: string };
    protected abstract _processParams?: { [key: string]: string };

    protected async request(method: Method, pathParam?: string, queryParam?: { [key: string]: string }, body?: { [key: string]: any } | FormData) {
        let url = `${isDev() ? DEV_BASE_URL : BASE_URL}/${this._contextPath}`;

        // console.log("@@ url: " + url)

        url = pathParam ? `${url}/${pathParam}` : url;
        url = queryParam ? `${url}?${new URLSearchParams(queryParam).toString()}` : url;
        let result: ResponseType = {
            flag: false,
            desc: "알 수 없는 오류가 발생하였습니다.",
        };
        try {
            // fetch data
            const headers = body instanceof FormData ? undefined : { "content-type": "application/json;charset=UTF-8" };
            const response = await fetch(url, {
                method,
                headers,
                body: body ? (body instanceof FormData ? body : JSON.stringify(body)) : undefined,
                credentials: "include",
            });

            if (response.ok) {
                // success
                const headers = response.headers;
                const contentType = headers.get("Content-Type");
                const contentDisposition = headers.get("Content-Disposition");

                if (response.status == StatusCodes.NO_CONTENT) {
                    return {
                        flag: true,
                        status: response.status,
                        desc: "조회된 데이터가 없습니다.",
                    };
                }

                switch (true) {
                    case !contentType:
                        throw new Error("응답 헤더에 Content-Type이 누락되었습니다.");
                    case contentDisposition?.includes(ContentDisposition.attachment):
                    case contentType?.includes(ContentType.excel):
                        result = {
                            flag: true,
                            result: await response.blob(),
                        };

                        if (contentDisposition?.includes("filename")) {
                            const header = contentDisposition.split(";").filter((str) => str.includes("filename"))[0];
                            const [, filename] = header.split("=");

                            result.filename = filename;
                        }

                        break;
                    case contentType?.includes(ContentType.json):
                        result = {
                            ...(await response.json()),
                        };
                        break;
                    case contentType?.includes(ContentType.csv):
                    case contentType?.includes(ContentType.html):
                    case contentType?.includes(ContentType.text):
                    case contentType?.includes(ContentType.xml):
                        result = {
                            flag: true,
                            result: await response.text(),
                        };
                        break;
                    default:
                        new Error("지원하지 않는 데이터 형식입니다.");
                }
            } else {
                // fail
                result = {
                    status: response.status,
                    ...(await response.json()),
                };
            }
        } catch (e) {
            // error
            result = {
                flag: false,
                desc: e instanceof Error ? e.message : String(e),
            };
        }

        return result;
    }

    protected async requestResource(pathParam: string | undefined, filename: string) {
        const url: string[] = [];

        url.push(isDev() ? DEV_BASE_URL : BASE_URL);
        url.push(this._contextPath);
        pathParam && url.push(pathParam);
        url.push(filename);

        let result: ResponseType = {
            flag: false,
            desc: "Uncaught Error",
        };

        try {
            // fetch data
            const response = await fetch(url.join("/"), {
                headers: {
                    "content-type": "application/json;charset=UTF-8",
                },
            });

            if (response.ok) {
                // success
                const headers = response.headers;
                const contentType = headers.get("Content-Type");
                const contentDisposition = headers.get("Content-Disposition");

                switch (true) {
                    case !contentType:
                        result = {
                            flag: true,
                            result: await response.json(),
                        };
                        break;
                    case contentDisposition?.includes(ContentDisposition.attachment):
                    case contentType?.includes(ContentType.excel):
                        result = {
                            flag: true,
                            result: await response.blob(),
                        };

                        if (contentDisposition?.includes("filename")) {
                            const header = contentDisposition.split(";").filter((str) => str.includes("filename"))[0];
                            const [, filename] = header.split("=");

                            result.filename = filename;
                        }

                        break;
                    case contentType?.includes(ContentType.json):
                        result = {
                            ...(await response.json()),
                        };
                        break;
                    case contentType?.includes(ContentType.csv):
                    case contentType?.includes(ContentType.html):
                    case contentType?.includes(ContentType.text):
                    case contentType?.includes(ContentType.xml):
                        result = {
                            flag: true,
                            result: await response.text(),
                        };
                        break;
                    default:
                        new Error("Unsupported data type");
                }
            } else {
                // fail
                result = {
                    status: response.status,
                    ...(await response.json()),
                };
            }
        } catch (e) {
            // error
            result = {
                flag: false,
                desc: e instanceof Error ? e.message : String(e),
            };
        }

        return result;
    }
}

export { type ResponseType, Gateway };
